import React, { useCallback, useEffect, useState } from "react";
import { getMyInquiry, markInquiryRead, fileUrl } from "../customerApi";
import { useMyText } from "./text";
import { useLanguage } from "../../i18n/LanguageContext";
import { fmtAt, fmtSchedule, isOpen, kindLabel } from "./format";
import { PageTop, StatusTag, ChannelTag, Steps, StateNote, ResultNote, LoadState } from "./parts";
import AppendForm from "./AppendForm";

function PhotoView({ src, onClose }) {
    const tt = useMyText();
    useEffect(() => {
        const k = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", k);
        return () => document.removeEventListener("keydown", k);
    }, [onClose]);
    return (
        <div className="my-photo-view" role="dialog" aria-modal="true" onClick={onClose}>
            <img src={src} alt="" onClick={(e) => e.stopPropagation()} />
            <button type="button" className="my-btn" onClick={onClose}>{tt("close")}</button>
        </div>
    );
}

// 문의 상세 #inq/{id} — 열면 「봤음」(§14-1 read) 후 배지를 다시 읽는다
export default function InquiryDetail({ id, go, customer, onChanged, onReask }) {
    const tt = useMyText();
    const { lang } = useLanguage();
    const [q, setQ] = useState(null);
    const [state, setState] = useState("loading");
    const [big, setBig] = useState(null);
    const load = useCallback(() => {
        setState("loading");
        return getMyInquiry(id)
            .then((d) => { setQ(d); setState("ok"); return true; })
            .catch((e) => { setState(e?.response?.status === 404 ? "notfound" : "error"); return false; });
    }, [id]);
    useEffect(() => {
        load().then((ok) => { if (ok) markInquiryRead(id).then(() => onChanged?.()); });
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
    // 덧붙이는 사이 닫힌 문의(409): 「읽는 중」으로 바꾸지 않고 조용히 다시 읽고, 안내는 남긴다
    const [closedNote, setClosedNote] = useState("");
    const onClosed = () => {
        setClosedNote(tt("err.inquiry_closed"));
        getMyInquiry(id).then(setQ).catch(() => {});
    };

    const top = <PageTop title={tt("detail.title")} customer={customer} onBack={() => go("inq")} />;
    if (state !== "ok") return <div className="my-wrap">{top}<LoadState state={state} onRetry={load} onList={() => go("inq")} /></div>;

    const names = (q.assignee_names || []).join(", ");
    const visit = isOpen(q.status) ? fmtSchedule(q.schedule, lang) : "";
    const rows = [
        [tt("f.kind"), kindLabel(q, lang)],
        [tt("f.assignee"), q.status !== "canceled" ? names : ""],
        [tt("f.visit"), visit],
        [tt("f.done"), q.status === "completed" ? fmtAt(q.completed_at, lang) : ""],
    ].filter(([, v]) => v);
    // 서버는 오래된 것부터 준다 — 뒤집어 최신이 위(같은 시각 줄이 있어 다시 정렬하지 않는다)
    const timeline = [...(q.timeline || [])].reverse();
    return (
        <div className="my-wrap">
            {top}
            <div className="my-sec">
                <div className="my-card-h">
                    <StatusTag status={q.status} /><ChannelTag channel={q.channel} /><span>No.{q.id}</span>
                    <span className="at">{tt("received", { when: fmtAt(q.created_at, lang) })}</span>
                </div>
                <div className="my-card-t full">{q.request_content}</div>
                <dl className="my-dl">{rows.map(([k, v]) => (<React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>))}</dl>
                {q.reopened_from_id && (
                    <button type="button" className="my-link" onClick={() => go(`inq/${q.reopened_from_id}`)}>{tt("reopened", { id: q.reopened_from_id })}</button>
                )}
            </div>
            <div className="my-sec">
                <Steps status={q.status} large />
                {q.status === "processing" && visit && names && <div className="my-note info">{tt("visitNote", { names, when: visit })}</div>}
                {(q.status === "on_hold" || q.status === "canceled") && <StateNote item={q} withResult={false} />}
            </div>
            {q.process_content && <div className="my-sec"><ResultNote item={q} /></div>}
            {(q.attachments || []).length > 0 && (
                <div className="my-sec">
                    <h3>{tt("photos", { n: q.attachments.length })}</h3>
                    <div className="my-photos">
                        {q.attachments.map((a, i) => (
                            <figure key={i}>
                                <img src={fileUrl(a.url)} alt={a.caption || ""} onClick={() => setBig(fileUrl(a.url))} />
                                <figcaption>{[a.by === "staff" ? a.by_name : tt("photoBy.customer"), a.caption].filter(Boolean).join(" · ")}</figcaption>
                            </figure>
                        ))}
                    </div>
                </div>
            )}
            <div className="my-sec">
                <h3>{tt("timeline")}</h3>
                <ul className="my-tl">
                    {timeline.map((l, i) => (
                        <li key={i} className={`${i === 0 ? "now" : ""} ${l.mine ? "mine" : ""}`}>
                            <div className="t">{l.text}</div><div className="d">{fmtAt(l.at, lang)}</div>
                        </li>
                    ))}
                </ul>
            </div>
            {closedNote && <p className="my-err" role="alert">{closedNote}</p>}
            {q.can_append && <AppendForm inquiry={q} onDone={(d) => { setQ(d); onChanged?.(); }} onClosed={onClosed} />}
            <div className="my-act">
                {q.status === "completed" && onReask && <button type="button" className="my-btn pri" onClick={() => onReask(q)}>{tt("reask")}</button>}
                {q.chat_session_id && <button type="button" className="my-btn" onClick={() => go(`log/${q.chat_session_id}`)}>{tt("viewChat")}</button>}
                <button type="button" className="my-btn" onClick={() => go("")}>{tt("home")}</button>
            </div>
            {big && <PhotoView src={big} onClose={() => setBig(null)} />}
        </div>
    );
}
